/**
 * /m/chassis/:chassisId — the field tech's port view.
 *
 * One card per ONU (GPON) or line (VDSL) with the state that matters
 * on site (state pill, signal / sync rate, subscriber GIG-ID, MAC,
 * last-seen time) + a per-line refresh button. State + RX tones come
 * from ``lib/signalTones`` — the same mappings the desktop inventory
 * tables use — and the GIG-ID gets its own row (with the occupancy
 * icon) so it can't be truncated away on a narrow screen. The header carries the
 * single chassis-wide action: "List ONUs" (GPON) or "Collect Line
 * Speed" (VDSL), gated on the adapter's declared capabilities from
 * GET /chassis/{id} — read-only adapters simply show no button.
 *
 * Job completion is tracked by POLLING GET /jobs/{id} every 2 s (the
 * platform's SSE stream isn't mounted anywhere — polling is the real
 * mechanism, same as the desktop InventoryTab) and the inventory query
 * is invalidated on terminal status so the cards refresh.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { api, apiErrorMessage } from "../../api/client";
import { formatLocal } from "../../lib/datetime";
import { Badge } from "../components/Badge";
import { OccupancyIcon } from "../components/OccupancyIcon";
import { RefreshLineButton } from "../components/RefreshLineButton";
import { useToast } from "../components/Toast";
import { VdslErrorChip } from "../components/VdslErrorChip";
import {
  gponStateTone,
  isVdslErrorFlagged,
  rxTone,
  VDSL_STATE_TONE,
  type VdslErrors7d,
} from "../lib/signalTones";

type ChassisOut = {
  id: number;
  effective_device_name: string;
  adapter_key: string;
  technology: string;
  enabled: boolean;
  capabilities: string[];
};

type InventoryOnu = {
  display_id: string;
  onu_or_ont_id: string | null;
  serial: string | null;
  state: string | null;
  last_rx_dbm: number | null;
  last_tx_dbm: number | null;
  last_sampled_at: string | null;
  last_seen_mac: string | null;
  last_seen_device: string | null;
  occupied: boolean | null;
  observed_at: string | null;
  missing_scans?: number | null;
};
type InventoryResponse = {
  ports: { port: string; onu_count: number; onus: InventoryOnu[] }[];
};

type VdslPortRow = {
  frame: number;
  slot: number;
  port: number;
  line_state: string | null;
  last_seen_mac: string | null;
  last_seen_device: string | null;
  occupied: boolean | null;
  up_act_rate_kbps: number | null;
  dw_act_rate_kbps: number | null;
  last_sample_at: string | null;
  observed_at: string | null;
  error_health?: string | null;
  errors_7d?: VdslErrors7d | null;
};
type VdslPortListResponse = { chassis_id: number; ports: VdslPortRow[] };

const TERMINAL = new Set(["succeeded", "failed", "partial", "cancelled"]);

/** Track N in-flight jobs (header scan + any per-row refreshes) with a
 * single 2 s poll loop; invalidate the inventory queries + toast on
 * each terminal status. Interval-based rather than per-job useQuery so
 * the hook count stays static however many rows are refreshing. */
function useJobTracker(chassisId: string | undefined) {
  const qc = useQueryClient();
  const toast = useToast();
  const [active, setActive] = useState<Record<string, string>>({});
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (Object.keys(active).length === 0) return;
    const timer = setInterval(async () => {
      for (const [key, jobId] of Object.entries(activeRef.current)) {
        try {
          const res = await api.get<{ status: string }>(`/jobs/${jobId}`);
          if (TERMINAL.has(res.data.status)) {
            setActive((m) => {
              const next = { ...m };
              delete next[key];
              return next;
            });
            qc.invalidateQueries({
              queryKey: ["v2", "chassis", chassisId, "inventory"],
            });
            qc.invalidateQueries({
              queryKey: ["v2", "chassis", chassisId, "vdsl-ports"],
            });
            toast.push(
              res.data.status === "succeeded"
                ? "Refresh complete"
                : `Job ${res.data.status}`,
              res.data.status === "succeeded" ? "success" : "error",
            );
          }
        } catch {
          /* transient poll failure — keep trying */
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [active, chassisId, qc, toast]);

  const submit = useCallback(
    async (key: string, name: string, params: Record<string, unknown>) => {
      try {
        const res = await api.post<{ id: string }>(
          `/chassis/${chassisId}/jobs`,
          { name, params },
        );
        setActive((m) => ({ ...m, [key]: res.data.id }));
      } catch (err) {
        toast.push(apiErrorMessage(err, "Could not queue the job"), "error");
      }
    },
    [chassisId, toast],
  );

  return { submit, busy: (key: string) => key in active, active };
}

export function MChassisPorts() {
  const { chassisId } = useParams<{ chassisId: string }>();
  const chassisQ = useQuery<ChassisOut>({
    queryKey: ["v2", "chassis", chassisId],
    queryFn: async () =>
      (await api.get<ChassisOut>(`/chassis/${chassisId}`)).data,
    enabled: !!chassisId,
  });
  const chassis = chassisQ.data;
  const isVdsl = chassis?.technology === "vdsl";

  const inventoryQ = useQuery<InventoryResponse>({
    queryKey: ["v2", "chassis", chassisId, "inventory"],
    queryFn: async () =>
      (await api.get<InventoryResponse>(`/chassis/${chassisId}/inventory`))
        .data,
    enabled: !!chassis && !isVdsl,
    refetchInterval: 30_000,
  });
  const vdslQ = useQuery<VdslPortListResponse>({
    queryKey: ["v2", "chassis", chassisId, "vdsl-ports"],
    queryFn: async () =>
      (
        await api.get<VdslPortListResponse>(
          `/chassis/${chassisId}/vdsl-ports`,
        )
      ).data,
    enabled: !!chassis && isVdsl,
    refetchInterval: 30_000,
  });

  const tracker = useJobTracker(chassisId);
  const caps = new Set(chassis?.capabilities ?? []);
  const headerJob = isVdsl
    ? caps.has("line_speed")
      ? { key: "scan", name: "collect_line_speed", label: "Collect Line Speed" }
      : null
    : caps.has("list_onus")
      ? { key: "scan", name: "list_onus", label: "List ONUs" }
      : null;

  if (chassisQ.isLoading) {
    return <p className="py-6 text-center text-sm text-slate-500">Loading…</p>;
  }
  if (!chassis) {
    return (
      <p className="py-6 text-center text-sm text-red-400">
        Chassis not found.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-slate-100">
            {chassis.effective_device_name}
          </h2>
          <span className="text-[11px] text-slate-500">
            {chassis.adapter_key}
            {!chassis.enabled && " · disabled"}
          </span>
        </div>
        {headerJob && (
          <button
            type="button"
            onClick={() => tracker.submit(headerJob.key, headerJob.name, {})}
            disabled={tracker.busy(headerJob.key) || !chassis.enabled}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-emerald-700 bg-emerald-900/40 px-3 text-xs font-medium text-emerald-100 active:bg-emerald-900/70 disabled:opacity-50"
          >
            <ListChecks className="h-4 w-4" />
            {tracker.busy(headerJob.key) ? "Running…" : headerJob.label}
          </button>
        )}
      </div>

      {isVdsl ? (
        <VdslCards
          rows={vdslQ.data?.ports ?? []}
          loading={vdslQ.isLoading}
          tracker={tracker}
        />
      ) : (
        <GponCards
          ports={inventoryQ.data?.ports ?? []}
          loading={inventoryQ.isLoading}
          tracker={tracker}
        />
      )}
    </div>
  );
}

type Tracker = ReturnType<typeof useJobTracker>;

function GponCards({
  ports,
  loading,
  tracker,
}: {
  ports: InventoryResponse["ports"];
  loading: boolean;
  tracker: Tracker;
}) {
  if (loading)
    return <p className="py-6 text-center text-sm text-slate-500">Loading…</p>;
  if (ports.length === 0)
    return (
      <p className="py-6 text-center text-sm text-slate-500">
        No ONUs in inventory — run List ONUs.
      </p>
    );
  return (
    <div className="flex flex-col gap-3">
      {ports.map((p) => (
        <section key={p.port} className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            PON {p.port} · {p.onu_count} ONU{p.onu_count === 1 ? "" : "s"}
          </h3>
          {p.onus.map((o) => {
            const key = `onu:${p.port}/${o.display_id}`;
            return (
              <div
                key={o.display_id}
                className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="font-mono font-medium text-slate-100">
                      {o.display_id}
                    </span>
                    <Badge tone={gponStateTone(o.state, o.missing_scans)}>
                      {(o.missing_scans ?? 0) > 0
                        ? `missing (${o.missing_scans}/3)`
                        : o.state ?? "?"}
                    </Badge>
                    {o.last_rx_dbm != null && (
                      <Badge tone={rxTone(o.last_rx_dbm)}>
                        RX {o.last_rx_dbm.toFixed(2)}
                      </Badge>
                    )}
                  </span>
                  <span className="flex min-w-0 items-center gap-1 text-xs">
                    <OccupancyIcon occupied={o.occupied} />
                    {o.last_seen_device ? (
                      <span className="truncate text-emerald-300">
                        {o.last_seen_device}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </span>
                  <span className="truncate font-mono text-[11px] text-slate-500">
                    {o.serial ?? "—"}
                    {o.last_seen_mac && ` · ${o.last_seen_mac}`}
                  </span>
                  <span className="text-[10px] text-slate-600">
                    seen {formatLocal(o.observed_at)}
                  </span>
                </div>
                <RefreshLineButton
                  busy={tracker.busy(key)}
                  onClick={() =>
                    tracker.submit(key, "refresh_onu", {
                      port: p.port,
                      display_id: o.display_id,
                    })
                  }
                  label={`Refresh ONU ${o.display_id}`}
                />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

function VdslCards({
  rows,
  loading,
  tracker,
}: {
  rows: VdslPortRow[];
  loading: boolean;
  tracker: Tracker;
}) {
  if (loading)
    return <p className="py-6 text-center text-sm text-slate-500">Loading…</p>;
  if (rows.length === 0)
    return (
      <p className="py-6 text-center text-sm text-slate-500">
        No VDSL lines in inventory — run Collect Line Speed.
      </p>
    );
  const fmtMbps = (kbps: number | null) =>
    kbps == null ? "—" : `${Math.round(kbps / 1000)}M`;
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((r) => {
        const key = `vdsl:${r.frame}/${r.slot}/${r.port}`;
        return (
          <div
            key={key}
            className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-2 text-sm">
                <span className="font-mono font-medium text-slate-100">
                  {r.frame}/{r.slot}/{r.port}
                </span>
                <Badge tone={VDSL_STATE_TONE[r.line_state ?? ""] ?? "neutral"}>
                  {r.line_state ?? "?"}
                </Badge>
                <VdslErrorChip health={r.error_health} errors={r.errors_7d} />
              </span>
              <span className="flex min-w-0 items-center gap-1 text-xs">
                <OccupancyIcon occupied={r.occupied} />
                {r.last_seen_device ? (
                  <span className="truncate text-emerald-300">
                    {r.last_seen_device}
                  </span>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </span>
              <span className="truncate font-mono text-[11px] text-slate-500">
                ↓{fmtMbps(r.dw_act_rate_kbps)} ↑{fmtMbps(r.up_act_rate_kbps)}
                {r.last_seen_mac && ` · ${r.last_seen_mac}`}
              </span>
              <span className="text-[10px] text-slate-600">
                sampled {formatLocal(r.last_sample_at)}
              </span>
              {/* Touch has no hover tooltip — spell the reasons out. */}
              {isVdslErrorFlagged(r.error_health) &&
                (r.errors_7d?.reasons?.length ?? 0) > 0 && (
                  <span
                    className={
                      r.error_health === "critical"
                        ? "text-[10px] text-red-300"
                        : "text-[10px] text-amber-300"
                    }
                  >
                    7d errors: {r.errors_7d?.reasons?.join(" · ")}
                  </span>
                )}
            </div>
            <RefreshLineButton
              busy={tracker.busy(key)}
              onClick={() =>
                tracker.submit(key, "refresh_port", {
                  frame: r.frame,
                  slot: r.slot,
                  port: r.port,
                })
              }
              label={`Refresh line ${r.slot}/${r.port}`}
            />
          </div>
        );
      })}
    </div>
  );
}
